/**
 * TMA Debug Logger
 *
 * Collects and sends detailed debug information about the Telegram Mini-App
 * authentication flow to Sentry. Helps diagnose issues where TMA users
 * get stuck in loading loops or fail to authenticate silently.
 */
import * as Sentry from '@sentry/vue'

/** Re-auth loop detection key in sessionStorage */
const TMA_REAUTH_COUNT_KEY = 'tma_reauth_count'
const TMA_REAUTH_TIMESTAMP_KEY = 'tma_reauth_ts'

/**
 * Collect Telegram WebApp context for Sentry
 */
export function getTelegramContext(): Record<string, unknown> {
    const webApp = window.Telegram?.WebApp
    const initDataUnsafe = webApp?.initDataUnsafe
    const user = initDataUnsafe?.user

    return {
        // Telegram environment
        tg_platform: webApp?.platform ?? 'unknown',
        tg_version: webApp?.version ?? 'unknown',
        tg_color_scheme: webApp?.colorScheme ?? 'unknown',
        tg_is_expanded: webApp?.isExpanded ?? false,

        // User info (safe to log)
        tg_user_id: user?.id ?? null,
        tg_username: user?.username ?? null,
        tg_first_name: user?.first_name ?? null,
        tg_language_code: user?.language_code ?? null,
        tg_is_premium: user?.is_premium ?? false,

        // Start param
        tg_start_param: initDataUnsafe?.start_param ?? null,

        // initData presence & length (don't log raw value — contains hash)
        has_init_data: !!webApp?.initData,
        init_data_length: webApp?.initData?.length ?? 0,

        // Auth query ID (for inline mode)
        has_query_id: !!initDataUnsafe?.query_id,

        // Chat type
        tg_chat_type: (initDataUnsafe as any)?.chat_type ?? null,
    }
}

/**
 * Collect app auth state context for Sentry
 */
export function getAuthStateContext(): Record<string, unknown> {
    const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'
    let currentToken: string | null = null
    try {
        currentToken = localStorage.getItem(storageKey)
    } catch { /* ignore */ }

    return {
        has_token: !!currentToken,
        token_length: currentToken?.length ?? 0,
        token_prefix: currentToken?.substring(0, 8) ?? null,
        tma_user_cookie: document.cookie.split('; ').some(c => c === 'tma_user=1'),
        current_path: window.location.pathname,
        current_href: window.location.href,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
    }
}

/**
 * Add a Sentry breadcrumb for TMA flow step
 */
export function tmaAddBreadcrumb(
    message: string,
    data?: Record<string, unknown>,
    level: 'info' | 'warning' | 'error' = 'info',
): void {
    Sentry.addBreadcrumb({
        category: 'tma.auth',
        message,
        data,
        level,
    })
    // Also log to console for dev debugging
    const prefix = '[TMA Debug]'
    if (level === 'error') {
        console.error(prefix, message, data)
    } else if (level === 'warning') {
        console.warn(prefix, message, data)
    } else {
        console.log(prefix, message, data)
    }
}

/**
 * Log the start of TMA auth attempt (call before API request)
 */
export function logTmaAuthStart(source: 'main.ts' | 'TmaInitPage'): void {
    const telegramCtx = getTelegramContext()
    const authCtx = getAuthStateContext()

    tmaAddBreadcrumb(`Auth start [${source}]`, {
        ...telegramCtx,
        ...authCtx,
    })

    Sentry.setTag('tma_debug', 'true')
    Sentry.setTag('tma_auth_source', source)
    Sentry.setContext('tma_telegram', telegramCtx)
    Sentry.setContext('tma_auth_state', authCtx)
}

/**
 * Log TMA auth API response
 */
export function logTmaAuthResponse(
    source: string,
    result: Record<string, unknown>,
): void {
    tmaAddBreadcrumb(`Auth response [${source}]`, {
        result: result.result,
        has_spa_auth_token: !!result.spa_auth_token,
        user_id: result.user_id ?? null,
        code: result.code ?? null,
        message: result.message ?? null,
        has_telegram_user: !!result.telegram_user,
        has_session_id: !!result.session_id,
    })
}

/**
 * Log token exchange result
 */
export function logTmaTokenExchange(
    source: string,
    success: boolean,
    tokenLength?: number,
    error?: unknown,
): void {
    if (success) {
        tmaAddBreadcrumb(`Token exchange success [${source}]`, {
            token_length: tokenLength ?? 0,
        })
    } else {
        tmaAddBreadcrumb(`Token exchange FAILED [${source}]`, {
            error: error instanceof Error ? error.message : String(error),
        }, 'error')
    }
}

/**
 * Log final auth state after flow completes
 */
export function logTmaAuthComplete(
    source: string,
    finalStatus: string,
    errorMessage?: string,
): void {
    const authCtx = getAuthStateContext()

    tmaAddBreadcrumb(`Auth complete [${source}]`, {
        final_status: finalStatus,
        error_message: errorMessage,
        ...authCtx,
    }, finalStatus === 'error' ? 'warning' : 'info')

    // If error — send event to Sentry immediately
    if (finalStatus === 'error') {
        Sentry.captureMessage(`TMA auth failed [${source}]: ${errorMessage ?? 'unknown'}`, {
            level: 'warning',
            tags: {
                tma_debug: 'true',
                tma_auth_source: source,
                tma_final_status: finalStatus,
            },
            contexts: {
                tma_telegram: getTelegramContext(),
                tma_auth_state: authCtx,
            },
        })
    }
}

/**
 * Detect and prevent re-auth loops.
 *
 * Returns `true` if it's safe to redirect to /tma-init.
 * Returns `false` if a loop is detected (already redirected recently).
 *
 * If loop detected, sends Sentry event with full diagnostics.
 */
export function canRedirectToTmaInit(): boolean {
    try {
        const countStr = sessionStorage.getItem(TMA_REAUTH_COUNT_KEY)
        const tsStr = sessionStorage.getItem(TMA_REAUTH_TIMESTAMP_KEY)
        const count = countStr ? parseInt(countStr, 10) : 0
        const ts = tsStr ? parseInt(tsStr, 10) : 0
        const now = Date.now()

        // Reset counter if last redirect was more than 60s ago
        if (now - ts > 60_000) {
            sessionStorage.setItem(TMA_REAUTH_COUNT_KEY, '1')
            sessionStorage.setItem(TMA_REAUTH_TIMESTAMP_KEY, String(now))
            return true
        }

        // If we've already redirected 2+ times in 60s — it's a loop
        if (count >= 2) {
            const telegramCtx = getTelegramContext()
            const authCtx = getAuthStateContext()

            Sentry.captureMessage('TMA re-auth loop detected', {
                level: 'error',
                tags: {
                    tma_debug: 'true',
                    tma_loop_detected: 'true',
                },
                contexts: {
                    tma_telegram: telegramCtx,
                    tma_auth_state: authCtx,
                    tma_loop: {
                        redirect_count: count,
                        first_redirect_ts: new Date(ts).toISOString(),
                        detection_ts: new Date(now).toISOString(),
                        elapsed_ms: now - ts,
                    },
                },
            })

            console.error('[TMA Debug] Re-auth loop detected!', {
                count,
                elapsed_ms: now - ts,
            })

            return false
        }

        // Increment counter
        sessionStorage.setItem(TMA_REAUTH_COUNT_KEY, String(count + 1))
        sessionStorage.setItem(TMA_REAUTH_TIMESTAMP_KEY, ts ? String(ts) : String(now))
        return true
    } catch {
        // sessionStorage not available — allow redirect
        return true
    }
}

/**
 * Report an actual exception to Sentry with full TMA context.
 * Uses `captureException` to preserve stack traces (unlike `captureMessage`).
 *
 * Use this in catch blocks where the original error object is available.
 */
export function logTmaException(
    error: unknown,
    source: string,
    extra?: Record<string, unknown>,
): void {
    const telegramCtx = getTelegramContext()
    const authCtx = getAuthStateContext()

    // Extract HTTP details if available
    const httpDetails: Record<string, unknown> = {}
    if (error && typeof error === 'object') {
        const err = error as any
        httpDetails.http_status = err?.response?.status ?? err?.status ?? null
        try {
            // Attempt to capture truncated response body for diagnostics
            const data = err?.response?.data
            if (data) {
                httpDetails.response_body = typeof data === 'string'
                    ? data.substring(0, 500)
                    : JSON.stringify(data).substring(0, 500)
            }
        } catch { /* ignore serialization errors */ }
    }

    tmaAddBreadcrumb(`Exception [${source}]`, {
        error_message: error instanceof Error ? error.message : String(error),
        ...httpDetails,
        ...extra,
    }, 'error')

    Sentry.captureException(error, {
        tags: {
            tma_debug: 'true',
            tma_auth_source: source,
        },
        contexts: {
            tma_telegram: telegramCtx,
            tma_auth_state: authCtx,
        },
        extra: {
            ...httpDetails,
            ...extra,
        },
    })
}

/**
 * Report a business-logic warning to Sentry with full TMA context.
 * Uses `captureMessage` at warning level — suitable for cases like
 * "no token received", "sponsor not found", "unexpected result".
 */
export function logTmaWarning(
    message: string,
    source: string,
    extra?: Record<string, unknown>,
): void {
    tmaAddBreadcrumb(`Warning [${source}]: ${message}`, extra, 'warning')

    Sentry.captureMessage(`TMA: ${message}`, {
        level: 'warning',
        tags: {
            tma_debug: 'true',
            tma_auth_source: source,
        },
        contexts: {
            tma_telegram: getTelegramContext(),
            tma_auth_state: getAuthStateContext(),
        },
        extra: { ...extra },
    })
}
